import React, { createContext, useContext, useId, useMemo } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import Heading, { type HeadingProps } from '@atlaskit/heading';
import { Flex, type FlexProps } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	rootStyles: {
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		paddingInlineEnd: token('space.200'),
		backgroundColor: token('elevation.surface'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
	},
	headerStyles: {
		columnGap: token('space.100'),
	},
	actionAreaStyles: {
		columnGap: token('space.100'),
	},
});

type ToolbarContextProps = { isInsideToolbarRoot: boolean; titleId?: string };
type RootProps = FlexProps;

const ToolbarContext = createContext<ToolbarContextProps>({ isInsideToolbarRoot: false });
const useIsInsideToolbarRoot = () => {
	const toolbarContext = useContext(ToolbarContext);

	if (toolbarContext === undefined) {
		// only happens if context root provider is not in parent component tree
		return false;
	}
	return toolbarContext.isInsideToolbarRoot === true;
};

const Root = ({ role, xcss, children, ...rest }: RootProps): React.JSX.Element => {
	// we allow consumers to override it, though we shouldn't
	// ref: https://www.w3.org/WAI/ARIA/apg/patterns/toolbar/#wai-ariaroles,states,andproperties
	const ariaRole = role ?? 'toolbar';
	const generatedTitleId = `toolbar-title-${useId()}`;
	const toolbarContextValue = useMemo(
		() => ({ isInsideToolbarRoot: true, titleId: generatedTitleId }),
		[generatedTitleId],
	);

	return (
		<ToolbarContext.Provider value={toolbarContextValue}>
			<Flex
				justifyContent="space-between"
				alignItems="center"
				aria-labelledby={generatedTitleId}
				xcss={cx(styles.rootStyles, xcss)}
				{...rest}
				role={ariaRole}
			>
				{children}
			</Flex>
		</ToolbarContext.Provider>
	);
};

type HeaderProps = FlexProps;

const Header = ({ xcss, children, ...rest }: HeaderProps): React.JSX.Element => {
	const isInsideToolbarRoot = useIsInsideToolbarRoot();
	if (!isInsideToolbarRoot) {
		throw new Error(
			`<Toolbar.Header> must be in a component tree that has <Toolbar.Root> as parent`,
		);
	}
	return (
		<Flex alignItems="center" xcss={cx(styles.headerStyles, xcss)} {...rest}>
			{children}
		</Flex>
	);
};

type TitleProps = Omit<HeadingProps, 'size'> & { size?: HeadingProps['size'] };

const Title = ({ id, size, children, ...rest }: TitleProps): React.JSX.Element => {
	const isInsideToolbarRoot = useIsInsideToolbarRoot();
	const toolbarContextData = useContext(ToolbarContext);
	if (!isInsideToolbarRoot) {
		throw new Error(
			`<Toolbar.Title> must be in a component tree that has <Toolbar.Root> as parent`,
		);
	}
	const { titleId } = toolbarContextData;
	// we need to explicitly put the children prop
	// as @atlassian/a11y/heading-has-content lint rule cannot be disabled
	return (
		<Heading size={size ?? 'medium'} {...rest} id={titleId}>
			{children}
		</Heading>
	);
};

type ActionGroupProps = FlexProps;

const ActionGroup = ({ xcss, children, ...rest }: ActionGroupProps): React.JSX.Element => {
	const isInsideToolbarRoot = useIsInsideToolbarRoot();
	if (!isInsideToolbarRoot) {
		throw new Error(
			`<Toolbar.ActionGroup> must be in a component tree that has <Toolbar.Root> as parent`,
		);
	}
	return (
		<Flex alignItems="center" xcss={cx(styles.actionAreaStyles, xcss)} {...rest}>
			{children}
		</Flex>
	);
};

/**
 * Public API with compound pattern:
 *
 * <Toolbar.Root>
 *   <Toolbar.Header>
 * 		<Toolbar.Title/>
 *   </Toolbar.Header>
 * 	 <Toolbar.ActionGroup>
 *   </Toolbar.ActionGroup>
 * </Toolbar.Root>
 */

const Toolbar = {
	Root,
	Header,
	Title,
	ActionGroup,
};

export { Toolbar };
